// import utils
import { getLocale } from 'utils/date';
import i18n from 'config/i18n';
import { areIntervalsOverlapping } from 'date-fns';
import { isNil } from 'ramda';
import * as yup from 'yup';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

// import types
import { CostCenterDetailFlag } from '../../types';

dayjs.extend(isSameOrAfter);

const t = i18n.getFixedT(getLocale());

export const planUnitSchema = yup.object({
  type: yup.string().required(t('validation.valueIsRequired')).default(''),
  category: yup.string().nullable().default(''),
  sortOrderAgency: yup.string(),
  colorCode: yup.string(),

  REQUEST_EXCLUDE: yup.boolean(),
  privilegedEmailAddress: yup
    .string()
    .test('is-valid', 'Invalid email addresses', (value) => {
      if (!value) {
        return true;
      }
      const emailSchema = yup.string().email();
      for (const email of value.split(';')) {
        if (!emailSchema.isValidSync(email.trim())) {
          return false;
        }
      }
      return true;
    })
});

const checkBoxSchema = {};

for (const key of Object.keys(CostCenterDetailFlag)) {
  checkBoxSchema[key] = yup.boolean().default(false);
}

const validSchema = yup.object({
  valid: yup.object({
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .typeError(t('invalidDate'))
      .nullable()
      .default(null)
      .min(yup.ref<Date>('from'), t('premiumPays.form.errors.invalidInterval'))
  })
});

const overlapValidator: yup.TestFunction<Date | null, any> = (
  val: Date | null,
  context: yup.TestContext<any>
) => {
  // we have 3 level deep object schema we get the second from the context array

  // @ts-ignore
  const current = context.from[1].value;

  const arrayToCompare =
    // @ts-ignore
    context.from.slice(-1)[0].value.costCenterDetails;

  if (!dayjs.isDayjs(current.valid.from) || !dayjs.isDayjs(current.valid.to))
    return true;

  const hasOverlap = arrayToCompare.some((fieldSet) => {
    if (fieldSet.brDynId === current.brDynId) return false;
    if (
      !dayjs.isDayjs(fieldSet.valid?.from) ||
      !dayjs.isDayjs(fieldSet.valid?.to)
    ) {
      return false;
    }

    return areIntervalsOverlapping(
      {
        start: current.valid.from.toDate(),
        end: current.valid.to.toDate()
      },
      {
        start: fieldSet.valid.from.toDate(),
        end: fieldSet.valid.to.toDate()
      }
    );
  });

  return !hasOverlap;
};

const validSchemaWithOverlapValidation = yup.object({
  valid: yup.object({
    from: yup
      .mixed()
      .test(
        'isRequired',
        t('validationFieldRequired', { name: t('validFrom') }),
        (value) => {
          return value && dayjs.isDayjs(value) && value.isValid();
        }
      )
      .test('overlap', t('db.error.exclusion_violation'), overlapValidator)
      .nullable()
      .default(null),

    to: yup
      .mixed()
      .test(
        'isRequired',
        t('validationFieldRequired', { name: t('validTo') }),
        (value) => {
          return value && dayjs.isDayjs(value) && value.isValid();
        }
      )
      .test(
        'min',
        t('premiumPays.form.errors.invalidInterval'),
        function (value) {
          const fromValue = this.parent.from;
          if (!dayjs.isDayjs(fromValue) || !dayjs.isDayjs(value)) {
            return true;
          }
          return dayjs(value).isSameOrAfter(dayjs(fromValue), 'day');
        }
      )
      .test('overlap', t('db.error.exclusion_violation'), overlapValidator)
      .nullable()
      .default(null)
  })
});

export const createCostCenterDetailSchema = yup.object({
  brDynId: yup.number().nullable().default(null),
  code: yup.string().required(t('validation.valueIsRequired')),
  name: yup.string().required(t('validation.valueIsRequired')),
  nameShort: yup
    .string()
    .max(
      15,
      t('validationMaxCharacters', {
        name: t('costCenterDetail.shortName'),
        number: 15
      })
    )
    .required(t('validation.valueIsRequired')),
  codeExt: yup.string(),
  codeExtSecondary: yup.string(),
  codeImp: yup.string(),
  codeImpSecondary: yup.string(),
  finBrDynId: yup
    .object()
    .shape({
      value: yup.number().nullable().default(null)
    })
    .nullable(),
  parentBrDynId: yup
    .object()
    .shape({
      value: yup.number().nullable().default(null)
    })
    .nullable(),
  benefitStartDate: yup
    .mixed()
    .transform((_, originalValue) => {
      if (originalValue == null || originalValue === '') return null;
      const parsed = dayjs(originalValue);
      return parsed.isValid() ? parsed : originalValue;
    })
    .test(
      'is-valid-dayjs',
      t('validationDateInvalid', {
        name: t('costCenterDetail.importStartDate')
      }),
      (value) => {
        if (value == null) return true;
        return dayjs.isDayjs(value) && value.isValid();
      }
    )
    .nullable()
    .default(null),
  flags: yup.object({ ...checkBoxSchema }),
  employerId: yup.number().nullable().default(null)
});

export const createPlanUnitSchema = planUnitSchema.concat(
  createCostCenterDetailSchema.concat(validSchema)
);

export const editPlanUnitSchema = planUnitSchema.concat(
  yup.object().shape({
    costCenterDetails: yup.lazy((arr) => {
      if (isNil(arr)) {
        return yup.array();
      }

      return yup
        .array()
        .of(
          createCostCenterDetailSchema.concat(validSchemaWithOverlapValidation)
        );
    })
  })
);
